// @oasis-permission-inherits: /basecamp (studio-edits:view)
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";

import { Skeleton } from "~/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { queryClient } from "~/lib/query-client";
import { InspectionDetail } from "~/modules/basecamp-run-inspector/basecamp-run-inspector-page";
import { recordReviewFeedback } from "~/modules/basecamp-style-reviews/decisions";
import { OutputReview } from "~/modules/basecamp-style-reviews/output-review";
import type { ReviewFeedback } from "~/modules/basecamp-style-reviews/schemas";
import { formatLatency } from "~/modules/basecamp-user-outputs/components";
import {
  submitUserOutputFeedbackMutation,
  userOutputInspectionQuery,
  userOutputPlaybackQuery,
  userOutputQuery,
  userOutputsQuery,
} from "~/modules/basecamp-user-outputs/queries";
import { userOutputSearchSchema, type UserOutput } from "~/modules/basecamp-user-outputs/schemas";

export const Route = createFileRoute("/_dash/basecamp/user-outputs/$operationId")({
  validateSearch: userOutputSearchSchema,
  component: UserOutputDetailPage,
});

function UserOutputDetailPage() {
  const { operationId } = Route.useParams();
  const search = Route.useSearch();
  const { data, error, isPending } = userOutputQuery.useQuery({
    pathParams: { operationId },
  });
  return (
    <div className="space-y-5 pb-12">
      <Link
        to="/basecamp/user-outputs"
        search={search}
        className="text-sm text-muted-foreground hover:underline"
      >
        ← Back to user outputs
      </Link>
      {isPending && <Skeleton className="h-96 w-full" />}
      {error && (
        <p role="alert" className="rounded-md border border-destructive p-4 text-destructive">
          Failed to load user output: {error.message}
        </p>
      )}
      {data && (
        <UserOutputDetail
          key={operationId}
          output={data.output}
          prompt={data.prompt}
          aspectRatio={data.aspectRatio}
          attachments={data.attachments}
          sourceAssetIds={data.sourceAssetIds}
        />
      )}
    </div>
  );
}

function UserOutputDetail({
  output,
  prompt,
  aspectRatio,
  attachments,
  sourceAssetIds,
}: {
  output: UserOutput;
  prompt: string | null;
  aspectRatio: string | null;
  attachments: Record<string, unknown>[];
  sourceAssetIds: string[];
}) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const pathParams = { operationId: output.operationId };
  const listParams = {
    params: {
      styleId: search.styleId || undefined,
      chosenTier: search.chosenTier === "any" ? undefined : search.chosenTier,
      limit: 30,
    },
  };
  const list = userOutputsQuery.useInfiniteQuery(listParams);
  const playback = userOutputPlaybackQuery.useQuery(
    { pathParams },
    { staleTime: 0, refetchOnWindowFocus: false, retry: false }
  );
  const inspection = userOutputInspectionQuery.useQuery({ pathParams });
  async function handleSaved(feedback: ReviewFeedback) {
    userOutputQuery.setQueryData(queryClient, { pathParams }, (current) =>
      current ? { ...current, output: recordReviewFeedback(current.output, feedback) } : current
    );
    if (feedback.rating !== null) {
      const following = (outputs: UserOutput[]) => {
        const index = outputs.findIndex((item) => item.operationId === output.operationId);
        return index < 0 ? undefined : outputs.slice(index + 1).find((item) => item.videoAssetId);
      };
      // A rating can land before the list's first page does.
      const loaded = list.data ? list : await list.refetch();
      let next = following(loaded.data?.pages.flatMap((page) => page.items) ?? []);
      // This output, or the next playable one, may be on a page not loaded yet.
      let hasNextPage = !loaded.isError && loaded.data?.pages.at(-1)?.nextCursor != null;
      while (!next && hasNextPage) {
        const fetched = await list.fetchNextPage();
        if (fetched.isError) {
          break;
        }
        next = following(fetched.data?.pages.flatMap((page) => page.items) ?? []);
        hasNextPage = fetched.hasNextPage;
      }
      if (next) {
        await navigate({
          to: "/basecamp/user-outputs/$operationId",
          params: { operationId: next.operationId },
          search,
        });
      }
    }
    await Promise.all([
      userOutputQuery.invalidateQuery(queryClient, { pathParams }),
      userOutputsQuery.invalidateQuery(queryClient, listParams),
    ]);
  }
  const scope = { id: `basecamp-user-output:${output.operationId}` };
  const submitNote = submitUserOutputFeedbackMutation.useMutation({
    scope,
    onSuccess: handleSaved,
  });
  const submitRating = submitUserOutputFeedbackMutation.useMutation({
    scope,
    onSuccess: handleSaved,
  });
  const styleName = output.styleName || output.styleId;
  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold">{styleName}</h1>
        <p className="break-all text-sm text-muted-foreground">
          {output.operationId} · {new Date(output.createdAt).toLocaleString()} ·{" "}
          {aspectRatio ?? "Unknown aspect ratio"}
        </p>
      </div>
      <dl className="grid grid-cols-1 gap-3 rounded-lg border p-4 text-sm sm:grid-cols-2 lg:grid-cols-4">
        {[
          ["Style", styleName],
          ["Chosen tier", output.chosenTier ?? "No tier"],
          ["Model", output.modelId ?? "Unknown"],
          ["Status", output.status],
          ["Latency", formatLatency(output.latencySeconds)],
          ["Project ID", output.projectId ?? "None"],
          ["Edit plan ID", output.editPlanId],
          ["Workflow ID", output.workflowId ?? "Unknown"],
          ["Project proto", output.projectProtoUri ?? "Unknown"],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="break-all">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="space-y-2">
        <h2 className="font-medium">Prompt</h2>
        <p className="whitespace-pre-wrap break-words rounded-lg border p-4 text-sm">
          {prompt || "No prompt recorded."}
        </p>
      </div>
      {output.status === "failed" && (
        <div role="alert" className="space-y-1 rounded-lg border border-destructive p-4 text-sm">
          <p className="font-medium">Create failed</p>
          <p className="whitespace-pre-wrap break-words">
            {output.error ?? "No error details were recorded."}
          </p>
        </div>
      )}
      <Tabs defaultValue="review">
        <TabsList>
          <TabsTrigger value="review">Review</TabsTrigger>
          <TabsTrigger value="inspect">Run inspector</TabsTrigger>
        </TabsList>
        <TabsContent value="review" className="space-y-5 pt-3">
          <OutputReview
            label={styleName}
            pathParams={pathParams}
            playback={playback}
            submitNote={submitNote}
            submitRating={submitRating}
            canReview={Boolean(output.videoAssetId)}
            supportsNotes
            feedback={output.feedback}
            notes={output.notes}
            sourceAssetIds={sourceAssetIds}
          />
          <details className="rounded-lg border p-4">
            <summary className="cursor-pointer font-medium">
              Attachments ({attachments.length})
            </summary>
            <pre className="mt-4 rounded bg-muted p-3 font-mono text-xs break-words whitespace-pre-wrap">
              {JSON.stringify(attachments, null, 2)}
            </pre>
          </details>
        </TabsContent>
        <TabsContent value="inspect" className="pt-3">
          {inspection.isPending && <p role="status">Loading the saved plan…</p>}
          {inspection.isError && (
            <p role="alert" className="text-sm text-destructive">
              Could not load this output's plan.
            </p>
          )}
          {inspection.data && (
            <InspectionDetail
              inspection={inspection.data}
              error={output.status === "failed" ? output.error : null}
              credits={null}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
